import type { IReview } from '@/types/content';

/** Ratings arrive normalised to 1–100; we show them as stars out of five. */
export const RATING_TO_STARS_MULTIPLIER = 0.05;
export const MAX_STARS = 5;

const average = (values: number[]) =>
	values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;

export interface ReviewStats {
	/** Average rating in stars, 0–5. */
	averageRating: number;
	/** How many reviews gave 1, 2, 3, 4 and 5 stars (index 0 is one star). */
	ratingScoreCount: number[];
	count: number;
}

export const toStars = (rating: number) => rating * RATING_TO_STARS_MULTIPLIER;

export const getReviewStats = (reviews: IReview[]): ReviewStats => {
	const ratingScoreCount = Array<number>(MAX_STARS).fill(0);
	reviews.forEach((review) => {
		const bucket =
			Math.min(
				MAX_STARS,
				Math.max(1, Math.round(toStars(review.rating))),
			) - 1;
		ratingScoreCount[bucket] += 1;
	});
	return {
		averageRating: toStars(average(reviews.map((r) => r.rating))),
		ratingScoreCount,
		count: reviews.length,
	};
};

export type RatingAdjective = 'excellent' | 'veryGood' | 'good' | null;

/** The word we put next to a score: 4+ stars is excellent, 3+ very good, 2+ good. */
export const ratingAdjective = (
	averageRating: number,
	total = MAX_STARS,
): RatingAdjective => {
	const fraction = total ? averageRating / total : 0;
	if (fraction >= 0.8) return 'excellent';
	if (fraction >= 0.6) return 'veryGood';
	if (fraction >= 0.4) return 'good';
	return null;
};

/** Newest first, and only reviews with something to read. */
export const reviewsForPreview = (reviews: IReview[], limit = 4) =>
	[...reviews]
		.filter((review) => review.primaryContent)
		.sort((a, b) => b.reviewCreatedAt.localeCompare(a.reviewCreatedAt))
		.slice(0, limit);
